/* eslint-disable no-param-reassign */
import _ from 'underscore';
import { odata } from '@bingads-webui/http-util';
import { odataQuery, odataUri, odataCast } from '@bingads-webui/odata-converter';
import { createResponseError } from '../data-utility/conversion-utility';

export class ODataBaseAdaptor {
  // All of the methods shown here must return a promise
  constructor(options = {}) {
    this.options = options;
  }

  getCommonOptions({
    ajaxOptions = {},
    urlParameters,
    host,
    path,
    headers,
    oDataToken,
  } = {}) {
    const options = _.defaults({}, ajaxOptions);
    if (_.isObject(urlParameters)) {
      options.urlParameters = urlParameters;
    }
    if (host) {
      options.host = host;
    }
    if (path) {
      options.path = path;
    }
    if (oDataToken) {
      options.oDataToken = oDataToken;
    }
    if (headers) {
      options.headers = headers;
    }
    return options;
  }

  convertQuery(queryParameters) {
    return odataQuery.convert(queryParameters);
  }

  find(definition, id, options) {
    _.extendOwn(options, this.options);
    let params = {};

    if (_.isObject(options)) {
      params = { data: _.omit(this.convertQuery(options), '$top', '$count') };
    }

    params.path = options.path;

    // Must resolve the promise with the found item
    return odata.get(odataUri.convert({
      definition, options, id, isFinding: true,
    }), _.defaults(params, this.getCommonOptions(options)))
      .then(result => result)
      .catch((result) => {
        const exception = createResponseError(
          result.status,
          result.body,
          result.statusText,
          result.responseText,
          result.errors
        );
        throw exception;
      });
  }

  findAll(definition, params, options = {}) {
    _.extendOwn(options, this.options);

    const request = odata.get(
      odataUri.convert({
        definition, options, isFinding: true,
      }),
      _.defaults(
        { data: this.convertQuery(_.defaults(params, { filterOptions: options.filterOptions })) },
        this.getCommonOptions(options)
      )
    );

    return request
      .then((result) => {
        // console.log(result);
        const addPropertyTotalCount = obj => Object.defineProperty(obj, 'totalCount', {
          value: result['@odata.count'],
          enumerable: false,
        });

        const addPropertyRaw = obj => Object.defineProperty(obj, 'raw', {
          value: result,
          enumerable: false,
        });

        // if OData results is just an array of string, make it an object then assign the
        // object to value.
        if (_.isString(_.first(result.value))) {
          result.value = _.map(result.value, item => ({ id: _.uniqueId('item-'), value: item }));
        }

        // JSData requires ID for every resource
        // Some calls to OData server won't return items with ID, or return items with an
        // empty string as id we add ID here to fit JSData requirement before JSData inject happens
        const queryId = _.uniqueId('query-');
        _.each(result.value, (item, index) => {
          if (!_.has(item, definition.idAttribute) || item[definition.idAttribute] === '') {
            item[definition.idAttribute] = `${queryId}-${index}`;
          }
        });

        const addProperties = _.compose(addPropertyTotalCount, addPropertyRaw);

        if (options.cacheResponse) {
          options.afterInject = _.compose(
            options.afterInject || _.identity,
            (innerOptions, injected) => addProperties(injected)
          );
        } else {
          // if odata response payload is not wrapped in value property,
          // move the contents under res.value
          result.value = _.has(result, 'value') ? result.value : result;
          addProperties(result.value);
        }

        return result.value;
      })
      .catch((result) => {
        const error = createResponseError(
          result.status,
          result.body,
          result.statusText,
          result.responseText,
          result.errors
        );
        throw error;
      });
  }

  get(definition, id, options) {
    _.extendOwn(options, this.options);
    let params = {};
    if (_.isObject(options)) {
      params = { data: this.convertQuery(options) };
    }
    params.path = options.path;

    return odata
      .get(
        odataUri.convert({ definition, options, id, isFinding: true }),
        _.defaults(params, this.getCommonOptions(options))
      )
      .then(result => result)
      .catch((result) => {
        const exception = createResponseError(
          result.status,
          result.body,
          result.statusText,
          result.responseText,
          result.errors
        );
        throw exception;
      });
  }

  create(definition, attrs, options) {
    _.extendOwn(options, this.options);
    let data = odataCast.data({ data: attrs, definition, options });
    let urlParameters = '';

    // Below if block is for the calls being made without EDM models
    // This enables making "POST" calls with query parameters without edm.
    if (attrs.queryParams) {
      urlParameters = this.convertQuery(attrs.queryParams);
      data = _.omit(data, 'queryParams');
    }

    // Must resolve the promise with the created item
    // NOTICE: added ajaxOptions here.
    return odata
      .post(
        odataUri.convert({ definition, options }),
        _.defaults(
          { data, urlParameters },
          this.getCommonOptions(options)
        )
      )
      .then(result => result)
      .catch((result) => {
        const error = createResponseError(
          result.status,
          result.body,
          result.statusText,
          result.responseText,
          result.errors
        );
        throw error;
      });
  }

  update(definition, id, attrs, options) {
    _.extendOwn(options, this.options);
    const data = odataCast.data({ data: attrs, definition, options });

    return odata
      .put(
        odataUri.convert({ definition, options, id }),
        _.defaults({ data }, this.getCommonOptions(options))
      )
      .then(result => result)
      .catch((result) => {
        const error = createResponseError(
          result.status,
          result.body,
          result.statusText,
          result.responseText,
          result.errors
        );
        throw error;
      });
  }

  post(definition, id, attrs, options) {
    _.extendOwn(options, this.options);
    const data = odataCast.data({ data: attrs, definition, options });

    return odata
      .post(
        odataUri.convert({ definition, options, id }),
        _.defaults({ data }, this.getCommonOptions(options))
      )
      .then(result => result)
      .catch((result) => {
        const error = createResponseError(
          result.status,
          result.body,
          result.statusText,
          result.responseText,
          result.errors
        );
        throw error;
      });
  }

  delete(definition, id, options) {
    _.extendOwn(options, this.options);
    let params = {};
    const queryParameters = { options };

    if (_.isObject(queryParameters)) {
      params = { data: _.omit(this.convertQuery(queryParameters), '$top', '$count') };
    }

    params.path = options.path;

    // Must resolve the promise with the found item
    return odata
      .delete(
        odataUri.convert({
          definition, options, id, // isFinding: true,
        }),
        _.defaults(params, this.getCommonOptions(options))
      )
      .then(result => result)
      .catch((result) => {
        const error = createResponseError(
          result.status,
          result.body,
          result.statusText,
          result.responseText,
          result.errors
        );
        throw error;
      });
  }
}
